import { ChatStrategy } from "./strategies/ChatStrategy";
import { OriginalChatStrategy } from "./strategies/OriginalChatStrategy";
import { SimplifiedChatStrategy } from "./strategies/SimplifiedChatStrategy";

export type ChatStrategyType = "original" | "simplified";

export class ChatContext {
  private currentStrategy: ChatStrategy;
  private strategies: Map<ChatStrategyType, ChatStrategy>;

  constructor() {
    this.strategies = new Map();
    this.initializeStrategies();
    this.currentStrategy = this.strategies.get("simplified")!;
  }

  private initializeStrategies(): void {
    // Initialize original strategy
    this.strategies.set("original", new OriginalChatStrategy());

    const simplifiedConfig = {
      baseUrl: import.meta.env.BUILD_API_CHAT_URL,
      headers: {
        "Content-Type": "application/json",
      },
      requiresAuth: false,
    };

    this.strategies.set(
      "simplified",
      new SimplifiedChatStrategy(simplifiedConfig),
    );
  }

  setStrategy(strategyType: ChatStrategyType): void {
    const strategy = this.strategies.get(strategyType);
    if (strategy) {
      this.currentStrategy = strategy;
      console.log(`Switched to ${strategy.getName()} strategy`);
    } else {
      throw new Error(`Strategy ${strategyType} not found`);
    }
  }

  getCurrentStrategy(): ChatStrategy {
    return this.currentStrategy;
  }

  getCurrentStrategyName(): string {
    return this.currentStrategy.getName();
  }

  getCurrentStrategyDescription(): string {
    return this.currentStrategy.getDescription();
  }

  getAvailableStrategies(): Array<{
    type: ChatStrategyType;
    name: string;
    description: string;
  }> {
    return Array.from(this.strategies.entries()).map(([type, strategy]) => ({
      type,
      name: strategy.getName(),
      description: strategy.getDescription(),
    }));
  }

  async sendMessage(message: string, additionalData?: any): Promise<any> {
    return this.currentStrategy.sendMessage(message, additionalData);
  }
}

// Singleton instance
export const chatContext = new ChatContext();
